import { Select, TooltipText } from '@/components/common';
import { ChevronDownIcon } from '@heroicons/react/outline';
import { StarIcon } from '@heroicons/react/solid';
import { startCase } from 'lodash-es';
import PropTypes from 'prop-types';
import { useState, useMemo, useCallback, useEffect } from 'react';
import ReactTooltip from 'react-tooltip';
import StepItem from './StepItem';
import STEP_RESULT_TYPE from './stepResultType';
import Switch from '@/modules/AdminTools/components/Switch';

const GROUP_BY = {
  TYPE: 'type',
  ENTITY: 'entity',
};
const GROUP_BY_OPTIONS = [
  { value: GROUP_BY.TYPE, label: 'Failure Type' },
  { value: GROUP_BY.ENTITY, label: 'Entity' },
];

const MANDATORY_FAILED_GROUP = 'mandatory_failed';

const StepsReport = ({
  report,
}) => {
  const [failureGroupBy, setFailureGroupBy] = useState(GROUP_BY.TYPE);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [showDependentEntities, setShowDependentEntities] = useState(false);

  const [successes, failures, hasDependencies] = useMemo(() => {
    const isGroupedByType = failureGroupBy === GROUP_BY.TYPE;

    const [successes, failures, hasDependencies] = report.steps.reduce(([successes, failures, hasDependencies], step, index) => {
      const stepWithOrder = { ...step, order: index + 1 };

      hasDependencies = !!step.dependent_entities;

      if (step.is_successful) {
        successes.push(stepWithOrder);
      } else {
        const groupKey = isGroupedByType
          ? (stepWithOrder.is_mandatory ? MANDATORY_FAILED_GROUP : stepWithOrder.type)
          : stepWithOrder.entity;

        if (!failures.groups[groupKey]) {
          failures.groups[groupKey] = [];
        }

        failures.groups[groupKey].push(stepWithOrder);
        failures.length++;
      }

      return [successes, failures, hasDependencies];
    }, [
      [],
      {
        groups: {
          ...isGroupedByType
            ? {
              [MANDATORY_FAILED_GROUP]: [],
              [STEP_RESULT_TYPE.INTERNAL_ERROR.value]: [],
              [STEP_RESULT_TYPE.EXTERNAL_ERROR.value]: [],
              [STEP_RESULT_TYPE.VALIDATION_FAILED.value]: [],
              [STEP_RESULT_TYPE.BROKEN_STATE.value]: [],
              [STEP_RESULT_TYPE.NOT_PROCESSED.value]: [],
            }
            : {},
        },
        length: 0,
      },
    ]);

    if (!isGroupedByType) {
      failures.groups = Object
        .keys(failures.groups)
        .sort()
        .reduce((acc, key) => ({ ...acc, [key]: failures.groups[key] }), {});
    }

    return [successes, failures, hasDependencies];
  }, [
    report.steps,
    failureGroupBy,
  ]);

  const toggleGroup = useCallback((groupName) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  }, []);

  const onGroupByChange = useCallback((event) => {
    setFailureGroupBy(event.target.value);
  }, []);

  useEffect(() => {
    // For some reason, in the case of modal, the ReactTooltip component mounts before its target component.
    // As a result, event listener doesn't bind to a target component.
    //
    // Calling ReactTooltip.rebuild() at here, i.e. in a parent component
    // will rebind event listeners for all the tooltip targets in a document and solve the problem.
    //
    // At this point both ReactTooltips and their target components are already mounted.
    //
    // Not need to call ReactTooltip.rebuild() on each render, so leave dependencies empty.
    ReactTooltip.rebuild();
  }, []);

  return (
    <div className="space-y-6">
      {failures.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between border-b border-gray-200 pb-2">
            <h3 className="text-base font-semibold text-gray-900">Failures ({failures.length})</h3>
            <div className="flex items-center gap-x-2">
              {hasDependencies && (
                <>
                  <label htmlFor="show-dependent-entities" className="text-sm text-gray-600">
                    Show dependent entities
                  </label>
                  <Switch
                    enabled={showDependentEntities}
                    onChange={(checked) => setShowDependentEntities(checked)}
                    id="show-dependent-entities"
                  />
                </>
              )}
              <label htmlFor="failure-group-by" className="text-sm text-gray-600 ml-4">
                Group by:
              </label>
              <Select
                inputId="failure-group-by"
                name="failureGroupBy"
                value={failureGroupBy}
                className="w-36"
                options={GROUP_BY_OPTIONS}
                onChange={onGroupByChange}
              />
            </div>
          </div>
          <div className="space-y-2">
            {Object.entries(failures.groups).map(([groupName, steps]) => {
              if (steps.length === 0) {
                return null;
              }

              const groupType = failureGroupBy === GROUP_BY.TYPE ? groupName : null;
              const { tooltip, Icon, iconColor } = groupType === MANDATORY_FAILED_GROUP ? {
                tooltip: 'These steps are mandatory and must succeed for the migration to complete.',
                Icon: StarIcon,
                iconColor: 'text-amber-400',
              } : STEP_RESULT_TYPE.find(groupType);
              const isExpanded = expandedGroups[groupName];

              return (
                <div key={groupName} className="border border-gray-200 rounded-md overflow-hidden">
                  <button
                    type="button"
                    onClick={() => toggleGroup(groupName)}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors duration-150 text-left"
                  >
                    <div className="flex items-center gap-x-3">
                      <div className="flex-shrink-0">
                        {tooltip ? (
                          <TooltipText
                            id={`steps-report-failure-group-${groupType}`}
                            text={<Icon className={`h-5 w-5 ${iconColor}`} aria-hidden="true" />}
                            message={tooltip}
                            className="max-w-sm"
                          />
                        ) : (
                          <Icon className={`h-5 w-5 ${iconColor}`} aria-hidden="true" />
                        )}
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-900">{startCase(groupName)}</span>
                        <span className="ml-2 text-xs text-gray-500">({steps.length} step{steps.length !== 1 ? 's' : ''})</span>
                      </div>
                    </div>
                    <ChevronDownIcon
                      className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                      aria-hidden="true"
                    />
                  </button>
                  <div
                    className={`grid transition-all duration-300 ease-in-out ${
                      isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="p-3 space-y-2 bg-white">
                        {steps.map((step, index) => (
                          <StepItem key={index} step={step} showDependentEntities={showDependentEntities} />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {successes.length > 0 && (
        <div className="space-y-3">
          <div className="border-b border-gray-200 pb-2">
            <h3 className="text-base font-semibold text-gray-900">Successes ({successes.length})</h3>
          </div>
          <div className="space-y-2">
            {successes.map((step, index) => (
              <StepItem key={index} step={step} showDependentEntities={showDependentEntities} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

StepsReport.propTypes = {
  report: PropTypes.shape({
    is_successful: PropTypes.bool.isRequired,
    steps: PropTypes.arrayOf(PropTypes.shape({
      is_successful: PropTypes.bool.isRequired,
      type: PropTypes.oneOf(STEP_RESULT_TYPE.values()).isRequired,
      entity: PropTypes.string.isRequired,
      key: PropTypes.string.isRequired,
      message: PropTypes.string.isRequired,
      context: PropTypes.arrayOf(PropTypes.string).isRequired,
      dependent_entities: PropTypes.arrayOf(PropTypes.string),
    })).isRequired,
  }).isRequired,
};

export default StepsReport;
